// src/pages/CheckoutPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import emailjs from '@emailjs/browser';
import './CheckoutPage.css';

const CheckoutPage = () => {
    const { cart, clearCart } = useCart();
    const navigate = useNavigate();
    
    const [customerInfo, setCustomerInfo] = useState({
        firstName: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        deliveryInstructions: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deliveryOption, setDeliveryOption] = useState('asap');
    const [deliveryTime, setDeliveryTime] = useState('');

    // EmailJS Configuration
    const EMAILJS_CONFIG = {
        SERVICE_ID: "service_h26smzo",
        TEMPLATE_ID: "template_z9pzos3",
        PUBLIC_KEY: "Bcw036Tf_0YFAt-R1",
        BUSINESS_EMAIL: "torpamax42@gmail.com",
        BUSINESS_NAME: "PotCastle's"
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCustomerInfo(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!customerInfo.firstName.trim()) newErrors.firstName = 'Full name is required';
        if (!customerInfo.phone.trim()) newErrors.phone = 'Phone number is required';
        if (!customerInfo.address.trim()) newErrors.address = 'Address is required';
        if (!customerInfo.city.trim()) newErrors.city = 'City is required';
        if (!customerInfo.state.trim()) newErrors.state = 'State is required';
        if (!customerInfo.zipCode.trim()) newErrors.zipCode = 'Zip code is required';

        // Simple phone validation
        const phoneDigits = customerInfo.phone.replace(/\D/g, '');
        if (customerInfo.phone && phoneDigits.length < 10) {
            newErrors.phone = 'Please enter a valid 10-digit phone number';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const sendOrderEmail = async (orderData) => {
        console.log('📧 Starting email send...');
        
        try {
            // Format cart items as plain text
            const cartItemsText = orderData.cart.map(item => 
                `• ${item.displayName || item.name} - Qty: ${item.quantity || 1} - $${(item.price || 0).toFixed(2)}`
            ).join('\n');

            // Create email parameters
            const templateParams = {
                to_email: EMAILJS_CONFIG.BUSINESS_EMAIL,
                from_name: EMAILJS_CONFIG.BUSINESS_NAME,
                reply_to: EMAILJS_CONFIG.BUSINESS_EMAIL,
                order_id: orderData.orderId,
                customer_name: orderData.customerInfo.firstName,
                customer_phone: orderData.customerInfo.phone,
                customer_address: `${orderData.customerInfo.address}, ${orderData.customerInfo.city}, ${orderData.customerInfo.state} ${orderData.customerInfo.zipCode}`,
                delivery_time: orderData.deliveryTime,
                delivery_instructions: orderData.customerInfo.deliveryInstructions || 'No special instructions',
                cart_items: cartItemsText,
                subtotal: `$${orderData.totals.subtotal.toFixed(2)}`,
                delivery_fee: orderData.totals.deliveryFee === 0 ? 'FREE' : `$${orderData.totals.deliveryFee.toFixed(2)}`,
                tax: '$0.00',
                total: `$${orderData.totals.total.toFixed(2)}`,
                order_date: orderData.orderDate
            };

            console.log('📧 Sending email...');

            // Send email using EmailJS
            const response = await emailjs.send(
                EMAILJS_CONFIG.SERVICE_ID,
                EMAILJS_CONFIG.TEMPLATE_ID,
                templateParams,
                EMAILJS_CONFIG.PUBLIC_KEY
            );

            console.log('✅ Email sent successfully!', response.status, response.text);
            return true;

        } catch (error) {
            console.error('❌ EmailJS error:', error);
            return false;
        }
    };

    const getCartTotal = () => {
        if (!Array.isArray(cart)) return 0;
        return cart.reduce((total, item) => total + ((item?.price || 0) * (item?.quantity || 1)), 0);
    };

    const getDeliveryFee = () => {
        const isWashingtonDC = customerInfo.state?.toUpperCase() === 'DC';
        
        // $20 fee for orders outside Washington DC
        if (!isWashingtonDC) {
            return 20;
        }
        
        // For DC orders: $10 fee if under $50, free if $50+
        return getCartTotal() >= 50 ? 0 : 10;
    };

    const getTotal = () => {
        return getCartTotal() + getDeliveryFee();
    };

    const handleOrderComplete = () => {
        clearCart();
        navigate('/');
    };

    const handleBackToShopping = () => {
        navigate('/products');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('🔄 Form submission started');

        if (!validateForm()) {
            alert('Please fix the form errors before submitting.');
            return;
        }

        setIsSubmitting(true);

        try {
            // Create unique order ID
            const orderId = `PC-${Date.now()}`;
            
            // Ensure cart is an array
            const safeCart = Array.isArray(cart) ? cart : [];
            
            // Create order data
            const orderData = {
                customerInfo: { ...customerInfo },
                cart: safeCart.map(item => ({ 
                    name: item.displayName || item.name,
                    price: item.price,
                    quantity: item.quantity,
                    selectedOption: item.selectedOption 
                })),
                deliveryTime: deliveryOption === 'asap' ? 'ASAP' : deliveryTime,
                orderId: orderId,
                orderDate: new Date().toLocaleString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                }),
                totals: {
                    subtotal: getCartTotal(),
                    deliveryFee: getDeliveryFee(),
                    tax: 0,
                    total: getTotal()
                }
            };

            console.log('📝 Order created:', orderData.orderId);

            // 1. Save to localStorage
            try {
                const existingOrders = JSON.parse(localStorage.getItem('PCOrders') || '[]');
                existingOrders.push(orderData);
                localStorage.setItem('PCOrders', JSON.stringify(existingOrders));
                console.log('💾 Saved to localStorage');
            } catch (storageError) {
                console.warn('⚠️ localStorage error:', storageError);
            }

            // 2. Try to send email
            let emailSent = false;
            
            console.log('📧 Attempting to send email...');
            try {
                emailSent = await sendOrderEmail(orderData);
                console.log('📧 Email send result:', emailSent ? 'SUCCESS' : 'FAILED');
            } catch (emailErr) {
                console.warn('⚠️ Email send threw error:', emailErr.message);
                emailSent = false;
            }

            // 3. Simulate processing
            await new Promise(resolve => setTimeout(resolve, 800));

            // 4. Log complete order details
            console.log('✅ ========== ORDER SUMMARY ==========');
            console.log(`Order ID: ${orderId}`);
            console.log(`Customer: ${customerInfo.firstName}`);
            console.log(`Phone: ${customerInfo.phone}`);
            console.log(`Address: ${customerInfo.address}, ${customerInfo.city}, ${customerInfo.state} ${customerInfo.zipCode}`);
            console.log(`Delivery: ${orderData.deliveryTime}`);
            console.log('Items:');
            safeCart.forEach(item => {
                console.log(`  • ${item.displayName || item.name} - $${item.price?.toFixed(2)} (Qty: ${item.quantity})`);
            });
            console.log(`Subtotal: $${getCartTotal().toFixed(2)}`);
            console.log(`Delivery Fee: $${getDeliveryFee().toFixed(2)}`);
            console.log(`Total: $${getTotal().toFixed(2)}`);
            console.log(`Email Status: ${emailSent ? '✅ SENT' : '❌ FAILED'}`);
            console.log('=====================================');

            // 5. Show success message
            let successMessage = `🎉 Order placed successfully!\n\n`;
            successMessage += `Order ID: ${orderId}\n`;
            successMessage += `Total: $${getTotal().toFixed(2)}\n`;
            successMessage += `\nOur team will contact you shortly at ${customerInfo.phone} to confirm delivery.\n`;
            
            if (!emailSent) {
                successMessage += `\n📝 Note: Order saved in system (check console for details).`;
                console.log('🔄 Order placed but email failed. Check localStorage for order data.');
            } else {
                successMessage += `\n✅ Confirmation sent to our team!`;
            }

            alert(successMessage);

            // 6. Complete the order
            handleOrderComplete();

        } catch (error) {
            console.error('❌ Unexpected error in order processing:', error);
            
            // Emergency save
            try {
                const emergencyData = {
                    firstName: customerInfo.firstName,
                    phone: customerInfo.phone,
                    address: customerInfo.address,
                    city: customerInfo.city,
                    state: customerInfo.state,
                    zipCode: customerInfo.zipCode,
                    cart: safeCart.map(item => ({
                        name: item.displayName || item.name,
                        price: item.price,
                        quantity: item.quantity
                    })),
                    total: getTotal(),
                    timestamp: new Date().toISOString(),
                    error: error.message
                };
                const emergencyOrders = JSON.parse(localStorage.getItem('emergencyOrders') || '[]');
                emergencyOrders.push(emergencyData);
                localStorage.setItem('emergencyOrders', JSON.stringify(emergencyOrders));
                console.log('🚨 Emergency order saved to localStorage');
            } catch (e) {
                console.error('Could not save emergency order:', e);
            }

            alert('⚠️ Order processing error. Please call us at (202) 276-4975 to complete your order.\n\nYour information has been saved for manual processing.');
            setIsSubmitting(false);
        }
    };

    // Check if cart is empty
    const safeCart = Array.isArray(cart) ? cart : [];
    
    if (safeCart.length === 0) {
        return (
            <div className="checkout-container">
                <div className="empty-cart-message">
                    <h2>Your cart is empty</h2>
                    <p>Add some products to your cart before checking out</p>
                    <button className="btn-primary" onClick={handleBackToShopping}>
                        Continue Shopping
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="checkout-container">
            <div className="checkout-header">
                <h1>Checkout</h1>
                <button className="back-button" onClick={handleBackToShopping}>
                    &larr; Back to Shopping
                </button>
            </div>

            <div className="checkout-content">
                <div className="checkout-form">
                    <form onSubmit={handleSubmit}>
                        {/* Customer Information */}
                        <div className="form-section">
                            <h2>Customer Information</h2>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="firstName">Full Name *</label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        value={customerInfo.firstName}
                                        onChange={handleInputChange}
                                        className={errors.firstName ? 'error' : ''}
                                        placeholder="John Smith"
                                    />
                                    {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phone">Phone Number *</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={customerInfo.phone}
                                        onChange={handleInputChange}
                                        className={errors.phone ? 'error' : ''}
                                        placeholder="(202) 000-0000"
                                    />
                                    {errors.phone && <span className="error-text">{errors.phone}</span>}
                                </div>
                            </div>
                        </div>

                        {/* Delivery Information */}
                        <div className="form-section">
                            <h2>Delivery Information</h2>
                            <div className="form-group">
                                <label htmlFor="address">Delivery Address *</label>
                                <input
                                    type="text"
                                    id="address"
                                    name="address"
                                    value={customerInfo.address}
                                    onChange={handleInputChange}
                                    className={errors.address ? 'error' : ''}
                                    placeholder="123 Main Street"
                                />
                                {errors.address && <span className="error-text">{errors.address}</span>}
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="city">City *</label>
                                    <input
                                        type="text"
                                        id="city"
                                        name="city"
                                        value={customerInfo.city}
                                        onChange={handleInputChange}
                                        className={errors.city ? 'error' : ''}
                                        placeholder="Washington"
                                    />
                                    {errors.city && <span className="error-text">{errors.city}</span>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="state">State *</label>
                                    <input
                                        type="text"
                                        id="state"
                                        name="state"
                                        value={customerInfo.state}
                                        onChange={handleInputChange}
                                        className={errors.state ? 'error' : ''}
                                        placeholder="DC"
                                    />
                                    {errors.state && <span className="error-text">{errors.state}</span>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="zipCode">ZIP Code *</label>
                                    <input
                                        type="text"
                                        id="zipCode"
                                        name="zipCode"
                                        value={customerInfo.zipCode}
                                        onChange={handleInputChange}
                                        className={errors.zipCode ? 'error' : ''}
                                        placeholder="20001"
                                    />
                                    {errors.zipCode && <span className="error-text">{errors.zipCode}</span>}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Delivery Time</label>
                                <div className="delivery-time-options">
                                    <label className="delivery-option">
                                        <input
                                            type="radio"
                                            name="deliveryOption"
                                            value="asap"
                                            checked={deliveryOption === 'asap'}
                                            onChange={(e) => setDeliveryOption(e.target.value)}
                                        />
                                        <span>As soon as possible (ASAP)</span>
                                    </label>
                                    <label className="delivery-option">
                                        <input
                                            type="radio"
                                            name="deliveryOption"
                                            value="custom"
                                            checked={deliveryOption === 'custom'}
                                            onChange={(e) => setDeliveryOption(e.target.value)}
                                        />
                                        <span>Specific time:</span>
                                        {deliveryOption === 'custom' && (
                                            <input
                                                type="time"
                                                value={deliveryTime}
                                                onChange={(e) => setDeliveryTime(e.target.value)}
                                                className="time-input"
                                                min="09:00"
                                                max="00:00"
                                            />
                                        )}
                                    </label>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="deliveryInstructions">Delivery Instructions (optional)</label>
                                <textarea
                                    id="deliveryInstructions"
                                    name="deliveryInstructions"
                                    value={customerInfo.deliveryInstructions}
                                    onChange={handleInputChange}
                                    placeholder="Building instructions, gate code, call on arrival, etc."
                                    rows="3"
                                />
                            </div>

                            <button
                                type="submit"
                                className="btn-primary submit-order-btn"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner"></span>
                                        Processing Order...
                                    </>
                                ) : (
                                    `Place Order - $${getTotal().toFixed(2)}`
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="order-summary">
                    <h2>Order Summary</h2>
                    <div className="order-items">
                        {safeCart.map((item, index) => (
                            <div key={`${item.id || index}-${item.key || index}`} className="order-item">
                                <img 
                                    src={item.image || 'https://images.unsplash.com/photo-1601164606564-dc66c6f7dd2c?w=100&h=100&fit=crop'} 
                                    alt={item.displayName || item.name} 
                                />
                                <div className="item-details">
                                    <h4>{item.displayName || item.name}</h4>
                                    <p>{item.strain || 'Product'} • Qty: {item.quantity || 1}</p>
                                    {item.selectedOption && (
                                        <p className="item-option">Option: {item.selectedOption.option}</p>
                                    )}
                                </div>
                                <div className="item-price">${((item.price || 0) * (item.quantity || 1)).toFixed(2)}</div>
                            </div>
                        ))}
                    </div>

                    <div className="order-totals">
                        <div className="total-row">
                            <span>Subtotal</span>
                            <span>${getCartTotal().toFixed(2)}</span>
                        </div>
                        <div className="total-row">
                            <span>Delivery Fee</span>
                            <span>{getDeliveryFee() === 0 ? 'FREE' : `$${getDeliveryFee().toFixed(2)}`}</span>
                        </div>
                        <div className="total-row final-total">
                            <span>Total</span>
                            <span>${getTotal().toFixed(2)}</span>
                        </div>
                    </div>

                    <div className="delivery-info">
                        <h3>📦 Delivery Information</h3>
                        <p>• Washington DC: $10 fee on orders under $50, free on $50+</p>
                        <p>• Outside DC: $20 delivery fee</p>
                        <p>• Hours: 9:00 AM - 12:00 AM daily</p>
                        <p>• Age Verification required for delivery</p>
                        <p>• Discreet packaging guaranteed</p>
                    </div>

                    <div className="contact-info">
                        <h3>📞 Need Help?</h3>
                        <p>Call us: (202) 276-4975</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;